import {
  involvesPerson,
  jobMatches,
  listSummary,
  withinDateFilter,
  type DateFilter,
  type Profile,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { JobRows, JobsSummary, ListHead } from "../../components/ui";
import { t } from "../../i18n";
import type { AppData } from "../shell/Shell";

/**
 * A flat list for staff who are not sales reps — a project manager or inspector
 * sees every job they are attached to, in any capacity.
 */
export function RepJobList({
  person,
  data,
  search,
}: {
  person: Profile;
  data: AppData;
  search: string;
}) {
  const [dateFilter, setDateFilter] = useState<DateFilter>("all");

  const rows = data.jobs
    .filter((job) => involvesPerson(job, person.id))
    .filter((job) => job.stage !== "lost")
    .filter((job) => withinDateFilter(job, dateFilter))
    .filter((job) => jobMatches(job, search));

  const values = data.values;
  const approvals = data.approvals;
  const summary = useMemo(
    () => (values ? listSummary(rows, values, new Date(), approvals) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows.map((job) => job.id).join(","), values, approvals],
  );

  return (
    <>
      <ListHead
        title={person.full_name ?? t("Staff")}
        count={rows.length}
        dateFilter={dateFilter}
        onDateFilter={setDateFilter}
      />
      <JobsSummary summary={summary} />
      <JobRows jobs={rows} values={values} />
    </>
  );
}
